import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import clientService from '../services/clientService';
import serviceService from '../services/serviceService';
import projectService from '../services/projectService';
import salesService from '../services/salesService';
import invoiceService from '../services/invoiceService';
import { useNotification } from '../hooks/useNotification';
import Modal, { ModalFormGrid, ModalFormColumn, FormGroup } from '../components/common/Modal';
import CustomSelect from '../components/common/CustomSelect';
import { useAuth } from '../context/AuthContext';
import { canAccessModule } from '../config/permissions';
import '../styles/FormSections.css';
import { 
  ArrowLeft, 
  User, 
  Mail, 
  Phone, 
  MapPin, 
  Calendar,
  Edit,
  Trash2,
  Plus,
  TrendingUp,
  FileText,
  Briefcase,
  Settings
} from 'lucide-react';

const ClientProfilePage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const { user } = useAuth();
  
  const isManagerOrAdmin = user && ['super-admin', 'gerente'].includes(user.role);
  const canSeeFinancialData = user && !['asesor-desarrollo'].includes(user.role);
  const isDev = user && user.role === 'asesor-desarrollo';
  
  const [client, setClient] = useState(null);
  const [services, setServices] = useState([]);
  const [projects, setProjects] = useState([]);
  const [sales, setSales] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [showEditModal, setShowEditModal] = useState(false);
  const [formData, setFormData] = useState({
    clientType: 'PERSON',
    name: '',
    email: '',
    phone: '',
    address: '',
    contactPerson: '',
    company: '',
    isActive: true
  });

  const loadClientData = useCallback(async () => {
    try {
      setLoading(true);
      
      // Cargar datos del cliente
      const clientResponse = await clientService.getClient(id);
      setClient(clientResponse.data || clientResponse);
      
      // Parallel fetch for all related data
      const [servicesRes, projectsRes, salesRes, invoicesRes] = await Promise.all([
        Promise.resolve({ services: [] }), // Removed nonexistent serviceService.getServicesByClient
        projectService.getProjects({ client: id }).catch((e) => { console.error('Proj Error', e); return { projects: [] }; }),
        salesService.getSales({ client: id }).catch((e) => { console.error('Sale Error', e); return { sales: [] }; }),
        invoiceService.getInvoices({ client: id }).catch((e) => { console.error('Inv Error', e); return { invoices: [] }; })
      ]);
      
      console.log('API Responses for Client Data:', { projectsRes, salesRes, invoicesRes });

      const extractItems = (res, key) => {
        if (!res) return [];
        if (Array.isArray(res[key])) return res[key];
        if (res.data && Array.isArray(res.data[key])) return res.data[key];
        if (res.data && Array.isArray(res.data)) return res.data;
        if (Array.isArray(res)) return res;
        return [];
      };

      setServices(extractItems(servicesRes, 'services'));
      setProjects(extractItems(projectsRes, 'projects'));
      setSales(extractItems(salesRes, 'sales'));
      setInvoices(extractItems(invoicesRes, 'invoices'));

    } catch (error) {
      console.error('Error cargando datos del cliente:', error);
      showNotification('Error al cargar los datos del cliente', 'error');
    } finally {
      setLoading(false);
    }
  }, [id, showNotification]);

  useEffect(() => {
    loadClientData();
  }, [loadClientData]);
  
  const getStatusBadge = (status) => {
    const statusColors = {
      PLANNING: { bg: '#FEF3C7', text: '#D97706', label: 'Planificación' },
      IN_PROGRESS: { bg: '#DBEAFE', text: '#2563EB', label: 'En Progreso' },
      ON_HOLD: { bg: '#F3F4F6', text: '#6B7280', label: 'En Pausa' },
      DELIVERED: { bg: '#D1FAE5', text: '#059669', label: 'Entregado' },
      CANCELLED: { bg: '#FEE2E2', text: '#DC2626', label: 'Cancelado' }
    };
    const style = statusColors[status] || { bg: '#F3F4F6', text: '#6B7280', label: status };
    return (
      <span style={{ 
        padding: '2px 8px', borderRadius: '12px', fontSize: '0.75rem', fontWeight: 600,
        backgroundColor: style.bg, color: style.text, textTransform: 'uppercase'
      }}>
        {style.label}
      </span>
    );
  };

  const getSaleStatusBadge = (status) => {
    const statusConfig = {
      'PENDING': { label: 'Pendiente', bg: '#FEF3C7', text: '#D97706' },
      'CONFIRMED': { label: 'Confirmada', bg: '#D1FAE5', text: '#059669' },
      'CANCELLED': { label: 'Cancelada', bg: '#FEE2E2', text: '#DC2626' },
      'COMPLETED': { label: 'Completada', bg: '#DBEAFE', text: '#2563EB' }
    };
    const style = statusConfig[status] || { bg: '#F3F4F6', text: '#6B7280', label: status };
    return (
      <span style={{ 
        padding: '2px 8px', borderRadius: '12px', fontSize: '0.75rem', fontWeight: 600,
        backgroundColor: style.bg, color: style.text, textTransform: 'uppercase'
      }}>
        {style.label}
      </span>
    );
  };

  const openEditModal = () => {
    if (client) {
      setFormData({
        clientType: client.clientType || 'PERSON',
        name: client.name || '',
        email: client.email || '',
        phone: client.phone || '',
        address: client.address || '',
        contactPerson: client.contactPerson || '',
        company: client.company || '',
        isActive: client.isActive !== false
      });
      setShowEditModal(true);
    }
  };

  const closeEditModal = () => {
    setShowEditModal(false);
    setFormData({
      clientType: 'PERSON',
      name: '',
      email: '',
      phone: '',
      address: '',
      contactPerson: '',
      company: '',
      isActive: true
    });
  };

  const handleInputChange = (e) => {
    const { name, value, type } = e.target;
    console.log('Input change:', name, value, type);
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? e.target.checked : 
              name === 'isActive' ? value === 'true' : value
    }));
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    
    try {
      await clientService.updateClient(id, formData);
      showNotification('Cliente actualizado exitosamente', 'success');
      closeEditModal();
      loadClientData(); // Recargar datos del cliente
    } catch (error) {
      console.error('Error actualizando cliente:', error);
      showNotification('Error al actualizar el cliente', 'error');
    }
  };


  const handleMoveToTrash = async () => {
    // Verificar si el cliente tiene servicios activos
    if (services && services.length > 0) {
      const activeServices = services.filter(service => service.status === 'ACTIVE');
      if (activeServices.length > 0) {
        showNotification(
          `No se puede enviar a papelera el cliente. Tiene ${activeServices.length} servicio(s) activo(s).`,
          'error'
        );
        return;
      }
    }

    // Confirmación con información adicional
    const confirmMessage = `¿Estás seguro de que quieres enviar a papelera el cliente "${client.name}"?\n\n` +
      `El cliente será movido a la papelera y podrás restaurarlo más tarde.\n\n` +
      `Esta acción no se puede deshacer automáticamente.`;

    if (window.confirm(confirmMessage)) {
      try {
        await clientService.deleteClient(id);
        showNotification('Cliente enviado a la papelera exitosamente', 'success');
        navigate('/clients');
      } catch (error) {
        console.error('Error enviando cliente a papelera:', error);
        showNotification('Error al enviar el cliente a la papelera', 'error');
      }
    }
  };

  if (loading) {
    return (
      <div className="client-profile-loading">
        <div className="loading-spinner"></div>
        <p>Cargando perfil del cliente...</p>
      </div>
    );
  }

  if (!client) {
    return (
      <div className="client-profile-error">
        <div className="error-content">
          <h2>Cliente no encontrado</h2>
          <p>El cliente que buscas no existe o ha sido eliminado.</p>
          <button 
            className="btn btn-primary"
            onClick={() => navigate('/clients')}
          >
            <ArrowLeft size={16} />
            Volver a Clientes
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="client-profile-page">
      {/* Header de Navegación */}
      <div className="profile-header">
        <div className="header-content">
          <button 
            className="btn btn-secondary btn-icon"
            onClick={() => navigate('/clients')}
          >
            <ArrowLeft size={20} />
            Volver
          </button>
          
          <div className="header-actions">
            {isManagerOrAdmin && !client.deletedAt && (
              <button 
                className="btn btn-danger btn-icon"
                onClick={handleMoveToTrash}
              >
                <Trash2 size={16} />
                Enviar a Papelera
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Header del Cliente */}
      <div className="client-header">
        <div className="client-info">
          <div className="client-avatar">
            <div className="avatar-circle">
              {client.name ? client.name.charAt(0).toUpperCase() : 'C'}
            </div>
          </div>
          <div className="client-details">
            <h1 className="client-name">{client.name}</h1>
            <p className="client-email">{client.email}</p>
            <div className="client-meta">
              <span className={`badge ${
                !client.isActive ? 'badge-error' : 'badge-success'
              }`}>
                {!client.isActive ? 'PAPELERA' : 'ACTIVO'}
              </span>
              <span className="client-phone">{client.phone}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs de Navegación */}
      <div className="profile-tabs">
        <button 
          className={`tab ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          <User size={16} />
          Resumen
        </button>
        <button 
          className={`tab ${activeTab === 'services' ? 'active' : ''}`}
          onClick={() => setActiveTab('services')}
        >
          <Settings size={16} />
          Servicios
        </button>
        <button 
          className={`tab ${activeTab === 'projects' ? 'active' : ''}`}
          onClick={() => setActiveTab('projects')}
        >
          <Briefcase size={16} />
          Proyectos
        </button>
        {canSeeFinancialData && (
          <button 
            className={`tab ${activeTab === 'sales' ? 'active' : ''}`}
            onClick={() => setActiveTab('sales')}
          >
            <TrendingUp size={16} />
            Ventas
          </button>
        )}
        {canSeeFinancialData && (
          <button 
            className={`tab ${activeTab === 'invoices' ? 'active' : ''}`}
            onClick={() => setActiveTab('invoices')}
          >
            <FileText size={16} />
            Facturas
          </button>
        )}
      </div>

      {/* Contenido de las Tabs */}
      <div className="profile-content">
        {activeTab === 'overview' && (
          <div className="overview-grid">
            {/* Card de Información Personal */}
            <div className="info-card">
              <div className="card-header">
                <h3>
                  <User size={20} />
                  Información Personal
                </h3>
                {isManagerOrAdmin && (
                  <button 
                    className="btn btn-sm btn-outline"
                    onClick={openEditModal}
                  >
                    <Edit size={14} />
                  </button>
                )}
              </div>
              <div className="card-content">
                <div className="info-row">
                  <Mail size={16} />
                  <span>{client.email}</span>
                </div>
                <div className="info-row">
                  <Phone size={16} />
                  <span>{client.phone || 'No especificado'}</span>
                </div>
                {client.address && (
                  <div className="info-row">
                    <MapPin size={16} />
                    <span>{client.address}</span>
                  </div>
                )}
                <div className="info-row">
                  <Calendar size={16} />
                  <span>Cliente desde {new Date(client.createdAt).toLocaleDateString()}</span>
                </div>
              </div>
            </div>

            {/* Card de Servicios */}
            <div className="info-card">
              <div className="card-header">
                <h3>
                  <Settings size={20} />
                  Servicios
                </h3>
                {isManagerOrAdmin && (
                  <button className="btn btn-sm btn-primary">
                    <Plus size={14} />
                    Agregar
                  </button>
                )}
              </div>
              <div className="card-content">
                {services.length > 0 ? (
                  <div className="services-list">
                    {services.slice(0, 3).map(service => (
                      <div key={service.id} className="service-item">
                        <div className="service-info">
                          <span className="service-name">{service.name}</span>
                          <span className="service-type">{service.type}</span>
                        </div>
                        <span className="service-status">{service.status}</span>
                      </div>
                    ))}
                    {services.length > 3 && (
                      <div className="more-services">
                        +{services.length - 3} servicios más
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="empty-state">
                    <Settings size={32} />
                    <p>No hay servicios registrados</p>
                    <button className="btn btn-sm btn-primary" onClick={() => navigate('/services/new')}>
                      <Plus size={14} />
                      Agregar Servicio
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Card de Proyectos */}
            <div className="info-card">
              <div className="card-header">
                <h3>
                  <Briefcase size={20} />
                  Proyectos
                </h3>
                {!isDev && (
                  <button className="btn btn-sm btn-primary" onClick={() => navigate('/proposals/new')}>
                    <Plus size={14} />
                    Nuevo
                  </button>
                )}
              </div>
              <div className="card-content">
                {projects.length > 0 ? (
                  <div className="services-list">
                    {projects.slice(0, 3).map(p => (
                      <div key={p.id || p._id} className="service-item" onClick={() => navigate(`/projects/${p.id || p._id}`)} style={{cursor: 'pointer'}}>
                        <div className="service-info">
                          <span className="service-name">{p.name}</span>
                          <span className="service-type">{p.overallProgress || 0}% de avance</span>
                        </div>
                        {getStatusBadge(p.status)}
                      </div>
                    ))}
                    {projects.length > 3 && (
                      <div className="more-services">+{projects.length - 3} proyectos más</div>
                    )}
                  </div>
                ) : (
                  <div className="empty-state">
                    <Briefcase size={32} />
                    <p>No hay proyectos registrados</p>
                    <button className="btn btn-sm btn-primary" onClick={() => navigate('/proposals/new')}>
                      <Plus size={14} />
                      Crear Proyecto (Propuesta)
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Card de Ventas */}
            {canSeeFinancialData && (
              <div className="info-card">
                <div className="card-header">
                  <h3>
                    <TrendingUp size={20} />
                    Ventas
                  </h3>
                </div>
                <div className="card-content">
                  <div className="stats-grid">
                    <div className="stat-item">
                      <span className="stat-value">${sales.reduce((s, sale) => s + (sale.total || 0), 0).toLocaleString('es-CO')}</span>
                      <span className="stat-label">Total Vendido</span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-value">{sales.length}</span>
                      <span className="stat-label">Transacciones</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Card de Facturas */}
            {canSeeFinancialData && (
              <div className="info-card">
                <div className="card-header">
                  <h3>
                    <FileText size={20} />
                    Facturas
                  </h3>
                </div>
                <div className="card-content">
                  <div className="stats-grid">
                    <div className="stat-item">
                      <span className="stat-value">{invoices.length}</span>
                      <span className="stat-label">Total Facturas</span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-value">${invoices.filter(i => i.paymentStatus !== 'PAID').reduce((s, i) => s + ((i.total || 0) - (i.paid || 0)), 0).toLocaleString('es-CO')}</span>
                      <span className="stat-label">Pendiente</span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'services' && (
          <div className="services-tab">
            <div className="tab-header">
              <h2>Servicios del Cliente</h2>
              <button className="btn btn-primary" onClick={() => navigate('/services/new')}>
                <Plus size={16} />
                Agregar Servicio
              </button>
            </div>
            <div className="services-grid">
              {services.map(service => (
                <div key={service.id} className="service-card">
                  <div className="service-header">
                    <h4>{service.name}</h4>
                    <span className={`service-status ${service.status}`}>
                      {service.status}
                    </span>
                  </div>
                  <div className="service-details">
                    <p><strong>Tipo:</strong> {service.type}</p>
                    <p><strong>Precio:</strong> ${service.price}</p>
                    {service.description && (
                      <p><strong>Descripción:</strong> {service.description}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'projects' && (
          <div className="projects-tab">
            <div className="tab-header">
              <h2><i className="lucide-briefcase"></i> Proyectos ({projects.length})</h2>
              <button className="btn btn-primary" onClick={() => navigate('/proposals/new')}>
                <Plus size={16} />
                Nuevo Proyecto
              </button>
            </div>
            {projects.length > 0 ? (
              <div className="services-grid">
                {projects.map(p => (
                  <div key={p._id || p.id} className="service-card" onClick={() => navigate(`/projects/${p._id || p.id}`)} style={{cursor: 'pointer'}}>
                    <div className="service-header">
                      <h4>{p.name}</h4>
                      {getStatusBadge(p.status)}
                    </div>
                    <div className="service-details">
                      <p><strong>Progreso:</strong> {p.overallProgress || 0}%</p>
                      <p><strong>Est. Fecha Entrega:</strong> {p.estimatedEndDate ? new Date(p.estimatedEndDate).toLocaleDateString() : 'No definida'}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="empty-state">
                <Briefcase size={64} />
                <h3>No hay proyectos</h3>
                <p>Este cliente aún no tiene proyectos asignados.</p>
                <button className="btn btn-primary" onClick={() => navigate('/proposals/new')}>
                  <Plus size={16} />
                  Crear Primer Proyecto
                </button>
              </div>
            )}
          </div>
        )}

        {activeTab === 'sales' && (
          <div className="sales-tab">
            <div className="tab-header">
              <h2><i className="lucide-trending-up"></i> Ventas ({sales.length})</h2>
            </div>
            {sales.length > 0 ? (
              <div className="services-grid">
                {sales.map(s => {
                  const itemsList = s.items && s.items.length > 0 
                    ? s.items.map(i => i.service?.name || i.description || 'Producto').join(' + ')
                    : 'Venta General';
                  
                  return (
                    <div key={s._id || s.id} className="service-card" onClick={() => navigate(`/sales/${s._id || s.id}`)} style={{cursor: 'pointer'}}>
                      <div className="service-header">
                        <h4>{itemsList}</h4>
                        {getSaleStatusBadge(s.status)}
                      </div>
                      <div className="service-details">
                        <p><strong>Fecha:</strong> {new Date(s.createdAt || s.date).toLocaleDateString()}</p>
                        <p><strong>Total:</strong> ${s.total?.toLocaleString('es-CO')}</p>
                        {s.items && s.items.length > 1 && (
                          <p style={{fontSize: '0.75rem', color: '#6B7280'}}>Contiene {s.items.length} artículos</p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="empty-state">
                <TrendingUp size={64} />
                <h3>No hay ventas registradas</h3>
                <p>Las ventas aparecerán aquí cuando se generen facturas o se confirmen ventas.</p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'invoices' && (
          <div className="invoices-tab">
            <div className="tab-header">
              <h2><i className="lucide-file-text"></i> Facturas ({invoices.length})</h2>
            </div>
            {invoices.length > 0 ? (
              <div className="services-grid">
                {invoices.map(i => (
                  <div key={i._id || i.id} className="service-card" onClick={() => navigate(`/invoices/${i._id || i.id}`)} style={{cursor: 'pointer'}}>
                    <div className="service-header">
                      <h4>{i.invoiceNumber || i.code || 'Factura'}</h4>
                      <span className={`service-status`} style={{backgroundColor: i.paymentStatus === 'PAID' ? '#10B981' : '#F59E0B'}}>{i.paymentStatus === 'PAID' ? 'Pagada' : 'Pendiente'}</span>
                    </div>
                    <div className="service-details">
                      <p><strong>Fecha:</strong> {new Date(i.createdAt || i.date).toLocaleDateString()}</p>
                      <p><strong>Total:</strong> ${i.total?.toLocaleString('es-CO')}</p>
                      <p><strong>Vence:</strong> {new Date(i.dueDate).toLocaleDateString()}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="empty-state">
                <FileText size={64} />
                <h3>No hay facturas</h3>
                <p>Las facturas aparecerán aquí cuando se emitan.</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Modal de Edición */}
      <Modal
        isOpen={showEditModal}
        onClose={closeEditModal}
        title="Editar Cliente"
        onSubmit={handleEditSubmit}
        submitText="Actualizar"
      >
        {/* Bloque 1: Tipo de Cliente */}
        <div className="form-section">
          <h4 className="form-section-title">Tipo de Cliente</h4>
          <CustomSelect
            label="Tipo de Cliente"
            value={formData.clientType}
            onChange={(value) => handleInputChange({ target: { name: 'clientType', value } })}
            options={[
              { value: 'PERSON', label: 'Persona Natural' },
              { value: 'COMPANY', label: 'Empresa' }
            ]}
            placeholder="Seleccionar tipo de cliente"
          />
        </div>

        {/* Bloque 2: Información Básica */}
        <div className="form-section">
          <h4 className="form-section-title">Información Básica</h4>
          <ModalFormGrid>
            <ModalFormColumn>
              <FormGroup label="Nombre" required htmlFor="name">
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className="form-input"
                  placeholder={formData.clientType === 'PERSON' ? "Nombre del cliente" : "Nombre de la empresa"}
                  required
                />
              </FormGroup>
              
              <FormGroup label="Email" required htmlFor="email">
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="form-input"
                  placeholder="email@ejemplo.com"
                  required
                />
              </FormGroup>
            </ModalFormColumn>

            <ModalFormColumn>
              <FormGroup label="Teléfono" htmlFor="phone">
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className="form-input"
                  placeholder="Número de teléfono"
                />
              </FormGroup>
            </ModalFormColumn>
          </ModalFormGrid>
        </div>

        {/* Bloque 3: Información de Empresa (solo si es empresa) */}
        {formData.clientType === 'COMPANY' && (
          <div className="form-section">
            <h4 className="form-section-title">Información de Empresa</h4>
            <FormGroup label="Persona de Contacto" htmlFor="contactPerson">
              <input
                type="text"
                id="contactPerson"
                name="contactPerson"
                value={formData.contactPerson}
                onChange={handleInputChange}
                className="form-input"
                placeholder="Nombre de la persona de contacto"
              />
            </FormGroup>
          </div>
        )}

        {/* Bloque 4: Información Adicional */}
        <div className="form-section">
          <h4 className="form-section-title">Información Adicional</h4>
          <FormGroup label="Dirección" htmlFor="address">
            <textarea
              id="address"
              name="address"
              value={formData.address}
              onChange={handleInputChange}
              className="form-input"
              placeholder="Dirección del cliente"
              rows="3"
            />
          </FormGroup>
        </div>

        {/* Bloque 5: Estado del Cliente */}
        <div className="form-section">
          <h4 className="form-section-title">Estado del Cliente</h4>
          <CustomSelect
            label="Estado"
            value={formData.isActive ? 'true' : 'false'}
            onChange={(value) => handleInputChange({ target: { name: 'isActive', value } })}
            options={[
              { value: 'true', label: 'Activo' },
              { value: 'false', label: 'Inactivo' }
            ]}
            placeholder="Seleccionar estado"
          />
          <p style={{fontSize: '12px', color: '#666', marginTop: '8px'}}>
            Estado actual: {formData.isActive ? 'Activo' : 'Inactivo'}
          </p>
        </div>
      </Modal>
    </div>
  );
};

export default ClientProfilePage;
